'use client';

import { c as _c } from "react/compiler-runtime";
import { type User } from '@/app/api/auth/[...nextauth]/next-auth';
import { Breadcrumbs } from '@/app/components/Breadcrumbs';
import { DrawerHeader, SideMenu } from '@/app/components/SideMenu';
import { AdminPages, GuestPages, SiteManagerPages, UserPages } from '@/app/components/SideMenuPages';
import { TopBar } from '@/app/components/TopBar';
import { VersionChangelogButton } from '@/app/components/VersionChangelogButton';
import { VersionSync } from '@/app/components/VersionSync';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import { ROOT_CONTAINER_ID } from '@shared/components/SideNavigationPanel';
import { UserRole } from '@shared/types';
import { signOut } from 'next-auth/react';
import { useEffect, useReducer, type ReactNode } from 'react';
import { type Changelog } from 'resonetics-webapps-modules';
import MuiXLicense from '@/app/components/MuiXLicense';
const getSideMenuItems = (isMenuOpen: boolean, newSuggestionsCount: number, access: {
  hasJmtAccess: boolean;
  hasQmtAccess: boolean;
}): Map<UserRole, ReactNode> => new Map<UserRole, ReactNode>([[UserRole.ADMIN, <AdminPages key={UserRole.ADMIN} isMenuOpen={isMenuOpen} newSuggestionsCount={newSuggestionsCount} />], [UserRole.USER, <UserPages key={UserRole.USER} isMenuOpen={isMenuOpen} {...access} />], [UserRole.GUEST, <GuestPages key={UserRole.GUEST} isMenuOpen={isMenuOpen} {...access} />], [UserRole.SITE_MANAGER, <SiteManagerPages key={UserRole.SITE_MANAGER} isMenuOpen={isMenuOpen} {...access} />]]);
const useAutoLogout = (lastLoginAt, timeToLogOut) => {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== lastLoginAt || $[1] !== timeToLogOut) {
    t0 = () => {
      const interval = setInterval(() => {
        if (!lastLoginAt || new Date().getTime() - lastLoginAt.getTime() <= timeToLogOut) {
          return;
        }
        signOut();
      }, 1000);
      return () => clearInterval(interval);
    };
    t1 = [lastLoginAt, timeToLogOut];
    $[0] = lastLoginAt;
    $[1] = timeToLogOut;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
};
const useDatadogLogs = (user, version) => {
  const $ = _c(11);
  let t0;
  let t1;
  if ($[0] !== version) {
    t0 = () => {
      if (process.env.NODE_ENV === "development") {
        return;
      }
      const token = process.env.NEXT_PUBLIC_DD_TOKEN;
      const env = process.env.NEXT_PUBLIC_DD_ENV;
      const service = process.env.NEXT_PUBLIC_DD_SERVICE;
      const applicationId = process.env.NEXT_PUBLIC_DD_APPLICATION_ID;
      const site = process.env.NEXT_PUBLIC_DD_SITE as 'datadoghq.com' | 'us3.datadoghq.com' || "datadoghq.com" as const;
      if (!token || !version) {
        return;
      }
      datadogLogs.init({
        clientToken: token,
        site,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: ["error", "warn"],
        sessionSampleRate: 100,
        storeContextsAcrossPages: true,
        beforeSend: _temp,
        env,
        service,
        version
      });
      const rumToken = process.env.NEXT_PUBLIC_DD_RUM_TOKEN;
      if (!rumToken || !applicationId) {
        return;
      }
      datadogRum.init({
        applicationId,
        clientToken: rumToken,
        site,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        allowedTracingUrls: [{
          match: window.location.origin,
          propagatorTypes: ["tracecontext"]
        }],
        storeContextsAcrossPages: true,
        defaultPrivacyLevel: "allow",
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        plugins: [reactPlugin({
          router: false
        })],
        service,
        env,
        version
      });
    };
    t1 = [version];
    $[0] = version;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== user.email || $[4] !== user.id || $[5] !== user.lastLoginAt || $[6] !== user.name || $[7] !== user.role || $[8] !== user.site) {
    t2 = () => {
      if (process.env.NODE_ENV === "development") {
        return;
      }
      datadogLogs.onReady(() => {
        datadogLogs.setUser({
          email: user.email,
          name: user.name,
          id: String(user.id)
        });
        datadogLogs.setUserProperty("role", user.role);
        datadogLogs.setUserProperty("site", user.site);
        datadogLogs.setUserProperty("lastLoginAt", user.lastLoginAt.toUTCString());
      });
      datadogRum.onReady(() => {
        datadogRum.setUser({
          email: user.email,
          name: user.name,
          id: String(user.id)
        });
        datadogRum.setUserProperty("role", user.role);
        datadogRum.setUserProperty("site", user.site);
        datadogRum.setUserProperty("lastLoginAt", user.lastLoginAt.toUTCString());
      });
      return _temp2;
    };
    t3 = [user.email, user.id, user.name, user.role, user.site, user.lastLoginAt];
    $[3] = user.email;
    $[4] = user.id;
    $[5] = user.lastLoginAt;
    $[6] = user.name;
    $[7] = user.role;
    $[8] = user.site;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
};
interface AuthenticatedLayoutProps {
  children: ReactNode;
  user: User;
  changelog: Changelog;
  newSuggestionsCount: number;
  lastLoginAt: Date | null;
  logOutTime: number;
  appVersion: string;
  env: 'development' | 'production' | 'test';
  muiKey: string;
}
export const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = t0 => {
  const $ = _c(42);
  const {
    children,
    user,
    changelog,
    newSuggestionsCount,
    lastLoginAt,
    logOutTime,
    appVersion,
    env,
    muiKey
  } = t0;
  const [isMenuOpen, toggleMenuOpen] = useReducer(_temp3, false);
  useAutoLogout(lastLoginAt, logOutTime);
  useDatadogLogs(user, appVersion);
  const hasJmtAccess = Boolean(user.jmtAccess);
  const hasQmtAccess = Boolean(user.qmtAccess);
  let t1;
  if ($[0] !== muiKey) {
    t1 = <MuiXLicense muiKey={muiKey} />;
    $[0] = muiKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== env || $[3] !== hasJmtAccess || $[4] !== hasQmtAccess || $[5] !== isMenuOpen || $[6] !== user.role) {
    t2 = <TopBar isMenuOpen={isMenuOpen} onToggleMenu={toggleMenuOpen} env={env} currentRole={user.role} jmtAccess={hasJmtAccess} qmtAccess={hasQmtAccess} />;
    $[2] = env;
    $[3] = hasJmtAccess;
    $[4] = hasQmtAccess;
    $[5] = isMenuOpen;
    $[6] = user.role;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== hasJmtAccess || $[9] !== hasQmtAccess || $[10] !== isMenuOpen || $[11] !== newSuggestionsCount || $[12] !== user.role) {
    t3 = getSideMenuItems(isMenuOpen, newSuggestionsCount, {
      hasJmtAccess,
      hasQmtAccess
    }).get(user.role);
    $[8] = hasJmtAccess;
    $[9] = hasQmtAccess;
    $[10] = isMenuOpen;
    $[11] = newSuggestionsCount;
    $[12] = user.role;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t3) {
    t4 = <Box flex={1}>{t3}</Box>;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      p: 1
    };
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== appVersion || $[18] !== changelog) {
    t6 = isANewVersion => <VersionChangelogButton changelog={changelog} isANewVersion={isANewVersion} appVersion={appVersion} />;
    $[17] = appVersion;
    $[18] = changelog;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== appVersion || $[21] !== t6) {
    t7 = <ListItem sx={t5}><VersionSync appVersion={appVersion}>{t6}</VersionSync></ListItem>;
    $[20] = appVersion;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t4 || $[24] !== t7) {
    t8 = <Box display="flex" flexDirection="column" minHeight="100%">{t4}{t7}</Box>;
    $[23] = t4;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== isMenuOpen || $[27] !== t8) {
    t9 = <SideMenu isMenuOpen={isMenuOpen} onToggleMenu={toggleMenuOpen}>{t8}</SideMenu>;
    $[26] = isMenuOpen;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      flexGrow: 1,
      p: 3,
      width: "auto",
      maxWidth: "1680px"
    };
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DrawerHeader />;
    t12 = <Breadcrumbs />;
    $[30] = t11;
    $[31] = t12;
  } else {
    t11 = $[30];
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Divider sx={{
      my: 2
    }} />;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== children) {
    t14 = <Container component="main" sx={t10} maxWidth={false} id={ROOT_CONTAINER_ID}>{t11}{t12}{t13}{children}</Container>;
    $[33] = children;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t14 || $[36] !== t2 || $[37] !== t9) {
    t15 = <Box component="div" display="flex">{t2}{t9}{t14}</Box>;
    $[35] = t14;
    $[36] = t2;
    $[37] = t9;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t1 || $[40] !== t15) {
    t16 = <>{t1}{t15}</>;
    $[39] = t1;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
};
function _temp(log) {
  log.hostname = "Browser";
  return true;
}
function _temp2() {
  datadogLogs.clearUser();
  datadogRum.clearUser();
}
function _temp3(prev) {
  return !prev;
}